import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { include } from "@omni/utils/object.utils";
import { createContext, ReactNode, use, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
    AddressFormContext,
    useAddressForm
} from "@/components/Address/AddressValidation/hooks/useAddressForm.hook";
import { useAddresses } from "@/components/Checkout/CheckoutCustomer/addresses/hooks/useAddresses.hook";
import { CheckoutCustomerFormValues } from "@/components/Checkout/CheckoutCustomer/checkoutCustomer.schema";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CheckoutCustomerDetailsFormModule from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.module";
import { TCheckoutCustomerDetailsFormState } from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.types";

export type AddressBookContextValues = {
    type: AddressType;
    disabledIds: Array<string>;
    addressFormOpen: boolean;
    addressListOpen: boolean;
    loading: boolean;
    addressId?: string;
    openAddressForm: (props: { type: AddressType; id?: string }) => void;
    openAddressList: (props: {
        type: AddressType;
        id?: string;
        disabledIds?: Array<string>;
    }) => void;
    select: (addressId: string, validate?: boolean) => Promise<void>;
    change: (addressId: string) => void;
    onAddressFormClose: () => void;
    onAddressListClose: () => void;
};

const AddressBookContext = createContext<AddressBookContextValues | undefined>(
    undefined
);

type Props = {
    children?: ReactNode;
};

export const AddressBookContextProvider = ({ children }: Props) => {
    const dispatch = useThunkDispatch();

    const [addressFormOpen, setAddressFormOpen] = useState(false);
    const [addressListOpen, setAddressListOpen] = useState(false);
    const [type, setType] = useState(AddressType.Default);
    const [addressId, setAddressId] = useState<string | undefined>(undefined);
    const [disabledIds, setDisabledIds] = useState<Array<string>>([]);

    const { reset } = useFormContext<CheckoutCustomerFormValues>();

    /**
     * Callback when the AddressForm modal is closed
     * - If the user has less than 2 addresses, the AddressList is irrelevant
     * - If the user has 2 or more addresses, we show the AddressList modal
     */
    const handleOnAddressFormClose = (id?: string) => {
        if (addresses.length < 2) {
            setAddressFormOpen(false);
        } else {
            handleOnOpenAddressList({ type: type, id: id || addressId });
        }
    };

    /**
     * Opens the AddressList modal, which allows the user to
     * - Choose an existing address
     * - Create a new address
     * - Edit an existing address
     * @param props.type The type of addresses to be selected
     * @param props.id The id of the preselected address, if not provided, the first non-disabled address will be selected
     * @param props.disabledIds The ids of the addresses to disable (eg. the delivery address should not be selectable when choosing a payment address for samePayment=false)
     */
    const handleOnOpenAddressList = ({
        type,
        id,
        disabledIds
    }: {
        type: AddressType;
        id?: string;
        disabledIds?: Array<string>;
    }) => {
        setAddressFormOpen(false);

        setType(type);
        setAddressListOpen(true);

        const firstAddress = addresses
            .filter(({ id }) => !disabledIds?.includes(id))
            .at(0);

        if (id) {
            setAddressId(id);
        } else if (firstAddress) {
            setAddressId(firstAddress.id);
        }

        // Only update the disabledIds if they are provided
        // This allows us to keep the same disabledIds when opening the modal multiple times without providing them again
        // eg. when opening the modal again after creating a new address in the AddressForm modal
        if (disabledIds) {
            setDisabledIds(disabledIds);
        }
    };

    const {
        errorFields,
        isValidating,
        validate,
        validateBusiness,
        openAddressModal,
        AddressFormModals
    } = useAddressForm({ onAddressChange: handleOnAddressFormClose });

    const { addresses, addressesLoading } = useAddresses();

    const _updateCheckoutCustomerForm = (
        values: Partial<TCheckoutCustomerDetailsFormState>
    ) => {
        dispatch(CheckoutCustomerDetailsFormModule.update(values));
    };

    /**
     * Callback when the AddressList modal is closed
     */
    const handleOnAddressListClose = () => {
        setAddressListOpen(false);
    };

    /**
     * Opens the AddressForm modal, which allows the user to add a new address or edit an existing one
     * @param props.type The type of the address to be added/edited
     * @param props.id The id of the address to edit, if not provided, the form will be opened in "add" mode
     */
    const handleOnOpenAddressForm = ({
        type,
        id
    }: {
        type: AddressType;
        id?: string;
    }) => {
        setAddressListOpen(false);

        setType(type);
        setAddressId(id);

        const address = id
            ? addresses.find(({ id: addressId }) => addressId === id)
            : undefined;
        openAddressModal(address);
    };

    /**
     * Callback when an address is created via the AddressForm modal
     * Opens the address list modal to allow the user to select the newly created address, with the new address preselected
     */
    const handleOnChange = (addressId: string) => {
        setTimeout(() =>
            handleOnOpenAddressList({ type: type, id: addressId })
        );
    };

    /**
     * Callback when an address is selected in the AddressList modal
     * - It validates the address if needed
     * - Calls handleOnSubmit to update the form and close the modals
     * @param addressId The id of the selected address
     * @param shouldValidate Whether to validate the address before selecting it
     */
    const handleOnSelect = async (
        addressId: string,
        shouldValidate: boolean = false
    ) => {
        const address = addresses.find(({ id }) => id === addressId);
        if (!address) return;

        if (
            !shouldValidate ||
            address.validationStatus === AddressValidationStatus.Validated ||
            address.validationStatus ===
                AddressValidationStatus.OverruledByCustomer ||
            address.validationStatus ===
                AddressValidationStatus.NoValidationNeeded
        ) {
            handleOnSubmit(address.id);

            return;
        }

        const validationResult = await validate({
            id: address.id,
            firstName: address.firstName,
            lastName: address.lastName,
            street: address.street,
            number: address.number,
            zipCode: address.zipCode,
            city: address.city,
            country: address.country,
            box: address.box,
            addressing: address.addressing,
            type: type,
            isBpostField: false,
            currentStatus: address.validationStatus
        });

        if (
            validationResult.validationStatus ===
            AddressValidationStatus.Invalid
        ) {
            handleOnOpenAddressForm({ type: type, id: address.id });
        } else if (
            validationResult.validationStatus !== AddressValidationStatus.Closed
        ) {
            handleOnSubmit(address.id);
        }
    };

    /**
     * Updates the checkout with the selected address and closes the modals
     * - Updates the form state with the selected address and recalculates samePaymentAddress
     * - Updates the redux state
     * @param addressId The id of the selected address
     */
    const handleOnSubmit = (addressId: string) => {
        const address = addresses.find(({ id }) => id === addressId);

        if (type === AddressType.Delivery) {
            _updateCheckoutCustomerForm({ deliveryAddressId: addressId });

            reset((prev) => ({
                ...prev,
                delivery: address,
                ...include(prev.payment, (payment) => ({
                    samePaymentAddress: payment.id === addressId
                }))
            }));
        } else if (type === AddressType.Payment) {
            _updateCheckoutCustomerForm({ paymentAddressId: addressId });

            reset((prev) => ({
                ...prev,
                payment: address,
                ...include(prev.delivery, (delivery) => ({
                    samePaymentAddress: delivery.id === addressId
                }))
            }));
        }

        handleOnAddressFormClose();
        handleOnAddressListClose();
    };

    const loading = addressesLoading || isValidating;

    return (
        <AddressBookContext
            value={{
                type: type,
                disabledIds: disabledIds,
                addressFormOpen: addressFormOpen,
                addressListOpen: addressListOpen,
                loading: loading,
                addressId: addressId,
                onAddressFormClose: handleOnAddressFormClose,
                onAddressListClose: handleOnAddressListClose,
                openAddressForm: handleOnOpenAddressForm,
                openAddressList: handleOnOpenAddressList,
                select: handleOnSelect,
                /** Updates the checkout with the selected address and closes the modals */
                change: handleOnChange
            }}
        >
            <AddressFormContext
                value={{
                    errorFields,
                    isValidating,
                    validate,
                    validateBusiness,
                    openAddressModal
                }}
            >
                {AddressFormModals}
                {children}
            </AddressFormContext>
        </AddressBookContext>
    );
};

export const useAddressBook = (): AddressBookContextValues => {
    const context = use(AddressBookContext);

    if (!isDefined(context)) {
        throw new Error(
            "useAddressBook must be used within an AddressBookContextProvider"
        );
    }

    return context;
};
